import * as React from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { FC, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { ISalesFunnelSummaryCard } from '@user/store/salesFunnelSummary/typings'
import { SalesFunnelSummaryMetrics } from './SalesFunnelSummaryMetrics'
import { SalesFunnelSummaryProjectedSales } from './SalesFunnelSummaryProjectedSales'
import {
  selectSalesFunnelSummaryDateRange,
  selectSalesFunnelFilters,
} from '@user/store/salesFunnelSummary/slice'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { SalesFunnelSummaryCard } from './SalesFunnelSummaryCard'
import { SoldServiceStatusSummaryPopover } from '@user/components/SoldServices'
import { useNavigate } from 'react-router-dom'
import Card from '@mui/material/Card'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'

const getStyles = (theme: Theme) => ({
  card: {
    padding: theme.spacing(2),
    width: '100%',
    height: '148px',
    lineHeight: '21px',
    marginTop: theme.spacing(1),
  },

  root: {
    backgroundColor: theme.palette.common.white,
    maxWidth: '1301px',
    margin: 'auto',
    marginBottom: theme.spacing(2.8),
    padding: theme.spacing(2),
    '@media (min-width: 1300px)': {
      padding: theme.spacing(0),
    },
  },

  title: {
    fontFamily: 'Source Sans Pro Semibold',
    fontSize: '28px',
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(0),
  },

  infoText: {
    fontSize: '14px',
    fontFamily: 'Source Sans Pro',
    paddingTop: theme.spacing(3),
    marginLeft: 'auto',
  },

  heading: {
    maxWidth: '1299px',
  },

  container: {
    width: '100%',
    backgroundColor: theme.palette.common.white,
  },
})

interface Props {
  summaryCard: ISalesFunnelSummaryCard
  handleClose: () => void
}
export const SalesFunnelSummary: FC<Props> = (props) => {
  const { summaryCard, handleClose } = props

  const { formatDate } = useLocalizationDateFormat()

  const { matchType } =
    useSelector(selectSalesFunnelFilters)?.filters?.createdDate?.value || {}

  const { fromCreatedDate, toCreatedDate } = useSelector(
    selectSalesFunnelSummaryDateRange,
  )

  const dateRangeText = useMemo(() => {
    if (matchType === 'bt' && !!fromCreatedDate) {
      const fromUTC = new Date(fromCreatedDate)
      const from = new Date(
        fromUTC.getTime() + fromUTC.getTimezoneOffset() * 60000,
      )

      const toUTC = toCreatedDate ? new Date(toCreatedDate) : new Date()
      const to = new Date(toUTC.getTime() + toUTC.getTimezoneOffset() * 60000)

      return `Date Range: ${formatDate(from)} - ${formatDate(to)}`
    } else if (matchType === 'lt' && !!toCreatedDate) {
      const toUTC = new Date(toCreatedDate)
      const to = new Date(toUTC.getTime() + toUTC.getTimezoneOffset() * 60000)
      return `Date Range: Before ${formatDate(to)}`
    } else if (matchType === 'gt' && !!fromCreatedDate) {
      const fromUTC = new Date(fromCreatedDate)
      const from = new Date(
        fromUTC.getTime() + fromUTC.getTimezoneOffset() * 60000,
      )
      return `Date Range: After ${formatDate(from)}`
    }

    return `Date Range: All Time`
  }, [fromCreatedDate, toCreatedDate, formatDate])

  const navigate = useNavigate()

  const handleManageClick = useCallback(
    (_status) => {
      handleClose()
      navigate('/opportunities')
    },
    [handleClose, navigate],
  )
  const styles = getStyles(useTheme())

  return (
    <Box sx={styles.container}>
      <Box sx={styles.root}>
        <Grid item>
          <Box display="flex" sx={styles.heading}>
            <Typography sx={styles.title}>Sales Funnel Summary</Typography>
            <Typography sx={styles.infoText}>{dateRangeText}</Typography>
          </Box>
        </Grid>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12} lg={4}>
            <SalesFunnelSummaryCard summaryCard={summaryCard} />
          </Grid>
          <Grid item xs={12} lg={8}>
            <SalesFunnelSummaryMetrics
              summaryCard={summaryCard}
              handleClose={handleClose}
            />
          </Grid>
        </Grid>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Card elevation={2} sx={styles.card}>
              <SalesFunnelSummaryProjectedSales
                summaryCard={summaryCard}
                handleClose={handleClose}
              />
              <SoldServiceStatusSummaryPopover
                onManageClick={handleManageClick}
                summaries={summaryCard.soldServiceDetails}
              />
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

SalesFunnelSummary.displayName = 'SalesFunnelSummary'
